import React, { CSSProperties, SyntheticEvent, useCallback, useEffect, useMemo } from "react";

import { Icon } from "semantic-ui-react";

import moment from "moment";
import { ComponentPropsGetterR } from "react-table-6";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import DropdownOptions from "react-lib/appcon/common/DropdownOptions";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";

import CardTrainingHistoryUX from "./CardTrainingHistoryUX";

import {
  ACTIONS,
  BTN_ACTS,
  OnEvent,
  PickedState,
  RunSequence,
  SetProp,
  State,
} from "./sequence/TrainingHistory";

import { COURSE_STATUS_OPTIONS, COURSE_TYPE_OPTIONS, getCourseStatusColor } from "./HRMInterface";

import { formatDate } from "react-lib/utils/dateUtils";

// Types
type CardTrainingHistoryProps = Pick<PickedState, "buttonLoadCheck" | "searchedItemListWithKey"> & {
  onEvent: OnEvent;
  setProp: SetProp;
  runSequence: RunSequence;
  TrainingHistorySequence: State["TrainingHistorySequence"];
  style?: CSSProperties;
  languageUX?: string;
};

const CardTrainingHistory = (props: CardTrainingHistoryProps) => {
  useEffect(() => {
    props.runSequence({ sequence: "TrainingHistory", restart: true });
  }, []);

  const mapOptions = useCallback(
    (items?: Record<string, any>[]) =>
      (items ?? []).map((item) => ({
        key: item.id,
        text: item.subject,
        value: item.id,
      })),
    []
  );

  const formattedItems = useMemo(
    () =>
      (props.TrainingHistorySequence?.pagination?.items ?? []).map((item) => {
        const typeName = COURSE_TYPE_OPTIONS.find(
          (option) => option.value === item.course_training_type
        );
        const statusName = COURSE_STATUS_OPTIONS.find(
          (option) => option.value === item.course_status
        );

        return {
          ...item,
          id: item.course_id,
          approveStatus: (
            <>
              {(() => {
                const totalApproved =
                  Number(item.attendance_period_approve) + Number(item.attendance_period_abstain);
                const coursePeriod = item.course_period ?? 0;

                if (totalApproved === 0) {
                  return <></>;
                } else if (totalApproved >= coursePeriod) {
                  return (
                    <span style={{ color: "rgba(0,0,0,.87)" }}>
                      {!!item.approve_status && (
                        <>
                          <span>Approved</span>
                          <Icon
                            style={{ marginLeft: "5px", marginTop: "-5px" }}
                            color="green"
                            name="check circle"
                          />
                        </>
                      )}
                    </span>
                  );
                }

                return (
                  <span style={{ color: "rgba(0,0,0,.87)", textTransform: "capitalize" }}>
                    Pending
                  </span>
                );
              })()}
            </>
          ),
          endDate: formatDate(moment(item.course_end_date)),
          participatedAbstainHr: (
            <>
              {(item.attendance_period_approve ?? 0) > 0 ||
              (item.attendance_period_abstain ?? 0) > 0 ? (
                <>
                  <span style={{ color: "#009b14" }}>
                    {(item.attendance_period_approve ?? 0) === 0
                      ? "0"
                      : String(item.attendance_period_approve ?? 0).padStart(2, "0")}
                  </span>
                  <span>/</span>
                  <span style={{ color: "rgba(0,0,0,.87)" }}>
                    {(item.attendance_period_abstain ?? 0) === 0
                      ? "0"
                      : String(item.attendance_period_abstain ?? 0).padStart(2, "0")}
                  </span>
                </>
              ) : (
                ""
              )}
            </>
          ),
          startDate: formatDate(moment(item.course_start_date)),
          statusName: statusName?.text || "",
          title: <div style={{ textAlign: "left" }}>{item.course_name}</div>,
          typeName: typeName?.text || "",
        };
      }),
    [props.TrainingHistorySequence?.pagination?.items]
  );

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;

    props.setProp("TrainingHistorySequence.filter", {
      ...props.TrainingHistorySequence?.filter,
      [name]: data.value,
    });
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "TrainingHistory",
      action: ACTIONS.SEARCH,
      activePage: props.TrainingHistorySequence?.pagination?.activePage,
    });
  };

  const handleGetTrProps: ComponentPropsGetterR = (state, rowInfo) => {
    const { original } = rowInfo ?? {};

    return {
      style: {
        color: getCourseStatusColor(original?.course_status),
        textAlign: "center",
      },
    };
  };

  const handleDropdownBlur = async (e: any, data: any) => {
    props.setProp("TrainingHistorySequence.filter.course_id", data.value);
  };

  return (
    <div style={{ ...props.style }}>
      <CardTrainingHistoryUX
        filter={props.TrainingHistorySequence?.filter}
        tableData={formattedItems}
        languageUX={props.languageUX}
        typeOptions={COURSE_TYPE_OPTIONS}
        getTrProps={handleGetTrProps}
        onChangeFilter={handleChangeFilter}
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="blue"
            paramKey={BTN_ACTS.SEARCH}
            size="small"
            title="SEARCH"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
            onClick={handleSearch}
          />
        }
        courseSearchBox={
          <SearchBoxWithKey
            onEvent={props.onEvent}
            id="CLC"
            defaultText={props.TrainingHistorySequence?.filter?.course_id}
            placeholder="Search Employee"
            selectedItem={props.TrainingHistorySequence?.filter?.course_id ?? null}
            type="TrainingCourse"
            inline
            searchedItemListWithKey={props.searchedItemListWithKey}
            mapOptions={mapOptions}
            setSelected={(value) => {
              props.setProp("TrainingHistorySequence.filter.course_id", value);
            }}
            onBlur={handleDropdownBlur}
          />
        }
        statusFilter={
          <DropdownOptions
            style={{ width: "100%" }}
            name="course_status"
            value={props.TrainingHistorySequence?.filter?.course_status ?? ""}
            clearable
            fluid
            multiple
            search
            options={COURSE_STATUS_OPTIONS}
            onChange={handleChangeFilter}
          />
        }
      />
    </div>
  );
};

CardTrainingHistory.displayName = "CardTrainingHistory";

export default React.memo(CardTrainingHistory);
